

import { useState, useEffect } from "react";
import { mastersApi } from "@/lib/api";
import SearchableDropdown from "../components/SearchableDropdown";

interface Service {
  id: number;
  name: string;
  base_price: number;
  selling_price?: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
  commission_percent?: number;
  commission_type?: string;
}

interface Vendor {
  id: number;
  name: string;
  commission_type?: string;
  commission_value?: number;
}

interface AddServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (service: { service_id: number; vendor_id?: number; base_price: number; selling_price: number; commission_percent?: number; commission_type?: string }) => void;
  services: Service[];
  vendors: Vendor[];
  existingServiceIds?: number[];
  isAdmin?: boolean;
}

export default function AddServiceModal({
  isOpen,
  onClose,
  onAdd,
  services,
  vendors,
  existingServiceIds = [],
  isAdmin = false,
}: AddServiceModalProps) {
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const selectedService = services.find(s => String(s.id) === selectedServiceId);
  const selectedVendor = vendors.find(v => String(v.id) === vendorId);
  const availableServices = services.filter(s => !existingServiceIds.includes(s.id));

  // Auto-populate vendor and selling price when service is selected
  useEffect(() => {
    if (selectedService) {
      // Auto-select vendor if service has a default vendor
      if (selectedService.vendor_id) {
        setVendorId(String(selectedService.vendor_id));
      } else {
        setVendorId("");
      }
      
      // Auto-populate selling price with service's selling price or base price
      const price = selectedService.selling_price || selectedService.base_price;
      setSellingPrice(String(price));
    } else {
      setVendorId("");
      setSellingPrice("");
    }
  }, [selectedService]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedServiceId) {
      newErrors.service = "Service is required";
    }

    if (!sellingPrice) {
      newErrors.selling_price = "Selling price is required";
    } else if (isNaN(parseFloat(sellingPrice)) || parseFloat(sellingPrice) <= 0) {
      newErrors.selling_price = "Selling price must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      if (selectedService) {
        const serviceData: any = {
          service_id: selectedService.id,
          vendor_id: vendorId ? parseInt(vendorId) : undefined,
          base_price: selectedService.base_price,
          selling_price: parseFloat(sellingPrice),
        };

        // Add commission data if admin
        if (isAdmin && selectedVendor) {
          serviceData.commission_percent = selectedVendor.commission_value || 0;
          serviceData.commission_type = selectedVendor.commission_type || "percentage";
        }

        onAdd(serviceData);
      }
      setSelectedServiceId("");
      setVendorId("");
      setSellingPrice("");
      onClose();
    } catch (error) {
      setErrors({ submit: "Failed to add service" });
    } finally {
      setLoading(false);
    }
  };

  // Calculate commission amount for display
  const commissionAmount = selectedVendor && sellingPrice
    ? selectedVendor.commission_type === "percentage"
      ? (parseFloat(sellingPrice) * (selectedVendor.commission_value || 0)) / 100
      : (selectedVendor.commission_value || 0)
    : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-stone-800">Add Service</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 flex items-center justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Service *</label>
            <SearchableDropdown
              label=""
              options={availableServices.map(s => ({
                value: String(s.id),
                label: `${s.name} - ₹${s.base_price.toLocaleString('en-IN')}`,
              }))}
              value={selectedServiceId}
              onChange={setSelectedServiceId}
              placeholder="Select service"
              error={errors.service}
            />
            {errors.service && <p className="text-xs text-rose-500 mt-1">{errors.service}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Vendor (Optional)</label>
            <SearchableDropdown
              label=""
              options={vendors.map(v => ({ value: String(v.id), label: v.name }))}
              value={vendorId}
              onChange={setVendorId}
              placeholder="Select vendor"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Selling Price *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value)}
              placeholder={selectedService ? `Base: ₹${selectedService.base_price.toLocaleString('en-IN')}` : "Enter selling price"}
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.selling_price
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.selling_price && <p className="text-xs text-rose-500 mt-1">{errors.selling_price}</p>}
          </div>

          {isAdmin && selectedVendor && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-stone-600">Commission</p>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {selectedVendor.commission_type === "percentage" 
                      ? `${selectedVendor.commission_value}%` 
                      : `₹${(selectedVendor.commission_value || 0).toLocaleString('en-IN')}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium text-amber-700">Amount</p>
                  <p className="text-sm font-bold text-amber-700">₹{commissionAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>
          )}

          {errors.submit && <p className="text-xs text-rose-500">{errors.submit}</p>}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg gold-gradient text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
